import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  model,
  signal,
  viewChildren,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { ClarityModule } from '@clr/angular';
import { User } from 'src/app/models/api/User';
import { Row } from 'src/app/models/ui/table';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { SetupConfig } from 'src/app/services/_offline/rep-setup.service';
import { APIService } from 'src/app/services/APIService/api.service';
import { ColumnFilterComponent } from '../../filter/column-filter/column-filter.component';
import { FILTER_TABLE_STORE, provideFilterStore } from 'src/app/shared/stores/filter-table.store';
import { unionBySort } from 'src/app/misc/functions';
import { FormsModule } from '@angular/forms';
import { IconDgCellComponent } from '../../_shared/datagrid-cell/icon-dg-cell.component';
import { AllSelectedBtnComponent } from '../../_shared/all-selected-btn/all-selected-btn.component';
import { PrüfungsTypStore } from 'src/app/services/Shared/prüfung-typ.store';

@Component({
  selector: 'app-replikat-setup',
  templateUrl: './replikat-setup.component.html',
  styleUrl: './replikat-setup.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ColumnFilterComponent,
    IconDgCellComponent,
    AllSelectedBtnComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
  ],
  providers: [provideFilterStore()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReplikatSetupComponent {
  protected readonly filterStore = inject(FILTER_TABLE_STORE);
  private readonly apiService = inject(APIService);
  private readonly KPRUVID = inject(PrüfungsTypStore).KPRUVID();

  readonly modalOpen = model.required();
  readonly setupConfig = model.required<SetupConfig>();

  private readonly columnFilters = viewChildren<ColumnFilterComponent>('objFilter');

  private readonly usersResource = rxResource({
    params: () => this.modalOpen() || undefined,
    stream: () => this.apiService.getUsers(),
  });

  protected readonly users = computed(() =>
    this.usersResource.hasValue() ? this.usersResource.value() : { columns: [], rows: [] }
  );
  protected readonly selectedUser = linkedSignal<User>(() => (this.isForSelf(), undefined));
  protected readonly isForSelf = signal(false);

  private readonly objekteResource = rxResource({
    params: () => (this.isForSelf() ? { id: 0 } : this.selectedUser()),
    stream: ({ params: { id: userId } }) => this.apiService.getAnlagen(this.KPRUVID, { userId, onlyEditRights: true }),
  });

  protected readonly isLoading = computed(() => this.usersResource.isLoading() || this.objekteResource.isLoading());

  protected readonly objekte = computed(() =>
    this.objekteResource.hasValue() ? this.objekteResource.value() : { columns: [], rows: [] }
  );
  protected readonly selectedObjekte = linkedSignal<Row[]>(() => (this.selectedUser(), []));
  protected readonly savedSelected = linkedSignal<Row[]>(() => (this.selectedUser(), []));

  protected replicate() {
    this.setupConfig.set({
      repType: 'Inspektion',
      IDs: this.savedSelected().map(x => +x.OSTAMMID),
      userId: this.selectedUser()?.id,
    });
    this.modalOpen.set(false);
  }

  constructor() {
    this.filterStore.registerTable(this.objekte);
    this.filterStore.registerFilters(this.columnFilters);

    effect(() => {
      const selected = this.selectedObjekte();
      if (selected.length) this.savedSelected.update(saved => unionBySort(selected, saved, 'OSTAMMID'));
    });
  }

  protected deleteFromSaved(index: number) {
    const row = this.savedSelected()[index];
    this.selectedObjekte.update(selected => {
      const index = selected.indexOf(row);
      if (index != -1) selected.splice(index, 1);
      return [...selected];
    });
    this.savedSelected.update(saved => (saved.splice(index, 1), [...saved]));
  }
}
